/** Elite affixes in the fight (DESIGN §5.1): the damage an elite takes, the slows it shrugs off, what it splits into. */
import { DamageType, EnemyAffixDef } from "../data/balance";
import { Immunity } from "../data/spawnRules";
import { Fix64 } from "../maths/fix64";
import { SimConstants } from "./constants";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { clearBurns, clearTickStatuses } from "./systems/enemyMotion";

/** The affix an enemy wears, or null for a plain one. */
export function affixOf(sim: LevelSim, e: Enemy): EnemyAffixDef | null { return e.affix >= 0 ? sim.balance.affixes[e.affix] ?? null : null; }

/** Damage of `type` after the wearer's affix (Warded blunts physical, Fireproof shrugs off burn). */
export function affixedDamage(sim: LevelSim, e: Enemy, damage: Fix64, type: DamageType): Fix64 {
  const a = affixOf(sim, e);
  if (a === null) return damage;
  const mult = type === DamageType.Physical ? a.physicalTaken : type === DamageType.Magic ? a.magicTaken : a.burnTaken;
  return mult.eq(Fix64.One) ? damage : damage.mul(mult);
}

/** False for an enemy a burn cannot catch (its affix takes no burn damage, or it is immune to fire, M36): no stack is lit, nothing spreads to it. */
export function canBurn(sim: LevelSim, e: Enemy): boolean { return (e.immunity & Immunity.Fire) === 0 && (affixOf(sim, e)?.burnTaken.isPositive() ?? true); }

/** A tower slow's strength on this enemy (Frostbound halves it). */
export function affixedSlow(sim: LevelSim, e: Enemy, fraction: Fix64): Fix64 {
  const a = affixOf(sim, e);
  return a === null || a.slowMult.eq(Fix64.One) ? fraction : fraction.mul(a.slowMult);
}

/**
 * Splitting: a killed elite leaves `splitCount` of its `splitInto` archetype where it fell, at the power it spawned at,
 * plain (a split never splits again), as far along the road as it was. A full enemy pool drops the rest rather than fail.
 */
export function splitOnDeath(sim: LevelSim, parent: Enemy): void {
  const a = affixOf(sim, parent);
  if (a === null || a.splitInto < 0 || a.splitCount <= 0) return;
  const arch = sim.balance.enemies[a.splitInto];
  for (let i = 0; i < a.splitCount; i++) {
    const e = sim.enemySlots.find((s) => !s.alive);
    if (e === undefined) return;
    e.id = sim.nextEnemyId++;
    e.archetype = arch.index; e.alive = true; e.isAir = arch.isAir; e.isBoss = false; e.isRare = false; e.affix = -1;
    e.power = parent.power;
    e.maxHp = arch.hp.mul(parent.power); e.hp = e.maxHp;
    e.speedPerTick = arch.speedCellsPerSecond.divInt(SimConstants.TicksPerSecond);
    e.armour = arch.armour; e.distance = parent.distance;
    e.points = arch.points; e.leakDamage = arch.leakDamage;
    e.slowFraction = Fix64.Zero; e.slowTicksLeft = 0;
    clearBurns(e);
    clearTickStatuses(e);
    e.shredStacks = 0; e.shredArmour = Fix64.Zero; e.boostTicksLeft = 0; e.boostCooldownLeft = 0; e.hitsTaken = 0;
    e.tolled = parent.tolled; e.attackCooldown = 0; e.shieldArmour = Fix64.Zero;
    e.x = parent.x; e.y = parent.y; e.cx = parent.cx; e.cy = parent.cy;
    sim._aliveEnemies++;
    sim.events.add(SimEventKind.EnemySpawned, e.id, e.archetype, 0);
  }
}
